import CantileverButton, {
  ChildrenContainer,
} from 'components/CantileverButton'
import {
  ConversationMessage,
  Source,
} from 'features/chatbot/contexts/ChatbotDataLayer'
import { FileText } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { AIResponseMessage } from './AIResponseMessage'

const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  max-width: 80%;
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`

const NameContainer = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const TextMessage = styled.div<{ $isUser: boolean }>`
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $isUser, theme }) =>
    $isUser ? theme.forecast.cardBackground : 'none'};
  border: 1px solid
    ${({ $isUser, theme }) =>
      $isUser ? theme.forecast.tertiaryText : 'transparent'};
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.forecast.warning};
`

const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  width: 100%;
`

const SourceCard = styled(CantileverButton)`
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  overflow: hidden;
  ${ChildrenContainer} {
    font-size: 0.75rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    overflow: hidden;
    min-width: 0;
  }
`

const SourceText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const FileTextIcon = styled(FileText)`
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
`

interface ConversationMessageComponentProps {
  message: ConversationMessage
  itemIndex: number
  onSourceClick: (source: Source) => void
}

export const ConversationMessageComponent: React.FC<
  ConversationMessageComponentProps
> = ({ message, itemIndex, onSourceClick }) => {
  const isUser = message.type === 'userMessage'
  const senderName = message.type === 'userMessage' ? 'You' : 'Cantilever AI'

  let messageContent: React.ReactNode

  switch (message.type) {
    case 'userMessage':
      messageContent = <TextMessage $isUser={true}>{message.text}</TextMessage>
      break
    case 'error':
      messageContent = <ErrorMessage>{message.text}</ErrorMessage>
      break
    case 'aiResponse':
      messageContent =
        message.naturalLanguageResponseBySegments &&
        message.naturalLanguageResponseBySegments.length > 0 ? (
          <AIResponseMessage
            segments={message.naturalLanguageResponseBySegments}
          />
        ) : (
          <ErrorMessage>No response from Delphi</ErrorMessage>
        )
      break
  }

  return (
    <>
      <MessageRow key={itemIndex} $isUser={isUser}>
        <NameContainer>{senderName}</NameContainer>
        {messageContent}
      </MessageRow>
      {message.type === 'aiResponse' &&
        message.sources &&
        message.sources.length > 0 && (
          <SourcesContainer key={`sources-${itemIndex}`}>
            {message.sources.map((source) => (
              <SourceCard key={source.id} onClick={() => onSourceClick(source)}>
                <FileTextIcon />
                <SourceText>{source.text}</SourceText>
              </SourceCard>
            ))}
          </SourcesContainer>
        )}
    </>
  )
}
